import styled from 'styled-components'
import Colors from 'Theme/Colors'
import Styles from './CardListItem.module.css'
import { IFText, IFButton, IFTextInput } from 'Components'
import React, { useState, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import Icon from '@mui/material/Icon'
import InfinityEnums from 'Enums/InfinityEnums'
import CheckIcon from '@mui/icons-material/CheckOutlined'
import CloseIcon from '@mui/icons-material/CloseOutlined'
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SelectPicker } from 'rsuite'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { styled as muiStyled } from '@mui/system'
import MuiAccordion from '@mui/material/Accordion'
import MuiAccordionSummary from '@mui/material/AccordionSummary'

const PenIcon = styled.div`
  color: ${Colors.OperatorUserListPenIcon};
`

const DeleteIcon = styled.div`
  color: ${Colors.OperatorUserListDeleteIcon};
`
const StatusContainer = styled.div`
  background-color: ${(props) => props.backgroundColor};
  color: ${(props) => props.color};
`

const Accordion = muiStyled(MuiAccordion)({
  backgroundColor: Colors.white,
  boxShadow: '0px 0px 0px 0px',
  borderRadius: '5px',
  '&:hover': {
    backgroundColor: Colors.ChargePointLogListItemHover,
  },
})

const AccordionSummary = muiStyled(MuiAccordionSummary)({
  padding: '0px',
  minHeight: 56,
  borderRadius: '5px',
  '&.Mui-expanded': {
    minHeight: 56,
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    height: '3.5rem',
    '&.Mui-expanded': {
      margin: 0,
    },
  },
})

const handleStatus = (status) => {
  switch (status) {
    case InfinityEnums.ChargingTokenStatus.ACTIVE:
      return Colors.ActiveCard
    case InfinityEnums.ChargingTokenStatus.INACTIVE:
      return Colors.InactiveCard
    case InfinityEnums.ChargingTokenStatus.DEACTIVATED:
      return Colors.DeActivatedCard
    default:
      return Colors.black
  }
}

const CardListItem = React.forwardRef(
  (
    {
      contractId,
      uid,
      type,
      status,
      onDeleteClick,
      isUpdateLoading,
      onUpdateClick,
      editPermission,
    },
    ref,
  ) => {
    const [accordExpanded, setAccordExpanded] = useState(false)
    const [isEdit, setIsEdit] = useState(false)
    const [contractIdEdit, setContractIdEdit] = useState(contractId)
    const [uidEdit, setUidEdit] = useState(uid)
    const [typeEdit, setTypeEdit] = useState(type)
    const [statusEdit, setStatusEdit] = useState(status)
    const [contractIdHover, setContractIdHover] = useState(false)
    const [RFIDHover, setRFIDHover] = useState(false)
    const [isContractIDCopied, setIsContractIDCopied] = useState(false)
    const [isRFIDCopied, setIsRFIDCopied] = useState(false)

    const animateContractID = () => {
      setIsContractIDCopied(!isContractIDCopied)
      setTimeout(() => setIsContractIDCopied(false), 2000)
    }

    const animateRFID = () => {
      setIsRFIDCopied(!isRFIDCopied)
      setTimeout(() => setIsRFIDCopied(false), 2000)
    }

    const OpenAccordion = () => {
      setIsEdit(true)
      setAccordExpanded(true)
    }

    const closeAccordion = () => {
      setAccordExpanded(false)
      setIsEdit(false)
    }

    const handleCancel = () => {
      setIsEdit(false)
      setAccordExpanded(false)
      setContractIdEdit(contractId)
      setUidEdit(uid)
      setTypeEdit(type)
      setStatusEdit(status)
    }

    const handleDone = () => {
      let newCard = {}
      if (contractIdEdit !== contractId) newCard.contractId = contractIdEdit
      if (uidEdit !== uid) newCard.uid = uidEdit
      if (typeEdit !== type) newCard.type = typeEdit
      if (statusEdit !== status) newCard.status = statusEdit

      if (Object.keys(newCard).length !== 0) onUpdateClick(newCard)
    }

    useImperativeHandle(ref, () => ({
      closeAccordion,
    }))

    let typeArray = []
    let statusArray = []
    typeArray = Object.values(InfinityEnums.ChargingTokenType).map((type) => {
      return {
        label: (
          <IFText
            key={Math.floor(Math.random() * 100)}
            style={{ color: Colors.text }}
          >
            {type}
          </IFText>
        ),
        value: type,
      }
    })
    statusArray = Object.values(InfinityEnums.ChargingTokenStatus).map(
      (status) => {
        return {
          label: (
            <IFText
              key={Math.floor(Math.random() * 100)}
              style={{ color: Colors.text }}
            >
              {status}
            </IFText>
          ),
          value: status,
        }
      },
    )

    const { t } = useTranslation()

    return (
      <Accordion expanded={accordExpanded}>
        <AccordionSummary>
          <div className={Styles.CardWrapper}>
            <div
              onMouseEnter={() => setContractIdHover(true)}
              onMouseLeave={() => setContractIdHover(false)}
              className={Styles.ContractIdContainer}
            >
              {isEdit ? (
                <div className={Styles.IFTextContainer}>
                  <IFTextInput
                    className={Styles.IFText}
                    value={contractIdEdit}
                    onChange={(e) => setContractIdEdit(e.target.value)}
                  />
                </div>
              ) : (
                <div className={Styles.ContractIdCopyContainer}>
                  <IFText className={Styles.wrapText}>{contractId}</IFText>
                  {contractIdHover && (
                    <div
                      className={Styles.iconContainer}
                      style={{ backgroundColor: Colors.red }}
                      onClick={() => navigator.clipboard.writeText(contractId)}
                    >
                      <CheckIcon
                        className={
                          isContractIDCopied ? Styles.visible : Styles.hide
                        }
                        style={{ color: Colors.primary }}
                      />
                      <ContentCopyIcon
                        onClick={animateContractID}
                        style={{ color: Colors.ClipboardCopy }}
                        className={
                          !isContractIDCopied ? Styles.visible : Styles.hide
                        }
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div
              onMouseEnter={() => setRFIDHover(true)}
              onMouseLeave={() => setRFIDHover(false)}
              className={Styles.UIDContainer}
            >
              {isEdit ? (
                <div className={Styles.IFTextContainer}>
                  <IFTextInput
                    className={Styles.IFText}
                    value={uidEdit}
                    onChange={(e) => setUidEdit(e.target.value)}
                  />
                </div>
              ) : (
                <div className={Styles.ContractIdCopyContainer}>
                  <IFText className={Styles.wrapText}>{uid}</IFText>
                  <div
                    className={Styles.iconContainer}
                    style={{ backgroundColor: Colors.white }}
                    onClick={() => navigator.clipboard.writeText(uid)}
                  >
                    {RFIDHover && (
                      <div>
                        <CheckIcon
                          className={
                            isRFIDCopied ? Styles.visible : Styles.hide
                          }
                          style={{ color: Colors.primary }}
                        />
                        <ContentCopyIcon
                          onClick={animateRFID}
                          style={{ color: Colors.ClipboardCopy }}
                          className={
                            !isRFIDCopied ? Styles.visible : Styles.hide
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className={Styles.TypeContainer}>
              {isEdit ? (
                <SelectPicker
                  className={Styles.SelectPicker}
                  appearance="default"
                  searchable={false}
                  value={typeEdit}
                  onSelect={(value) => setTypeEdit(value)}
                  data={typeArray}
                  cleanable={false}
                  size="md"
                />
              ) : (
                <IFText>{type}</IFText>
              )}
            </div>

            <div className={Styles.StatusWrapper}>
              {isEdit ? (
                <SelectPicker
                  className={Styles.SelectPicker}
                  appearance="default"
                  searchable={false}
                  value={statusEdit}
                  onSelect={(value) => setStatusEdit(value)}
                  data={statusArray}
                  cleanable={false}
                  size="md"
                />
              ) : (
                <StatusContainer
                  backgroundColor={
                    isEdit ? Colors.transparent : Colors.GreyBackground
                  }
                  color={handleStatus(status)}
                  className={Styles.StatusContainer}
                >
                  <IFText className={Styles.StatusText}>{status}</IFText>
                </StatusContainer>
              )}
            </div>

            {!accordExpanded ? (
              editPermission && (
                <PenIcon className={Styles.PenIconContainer}>
                  <FontAwesomeIcon
                    onClick={OpenAccordion}
                    className={Styles.PenIcon}
                    icon={faPen}
                  />
                </PenIcon>
              )
            ) : (
              <DeleteIcon className={Styles.DeleteIconContainer}>
                <FontAwesomeIcon
                  className={Styles.DeleteIcon}
                  icon={faTrash}
                  onClick={(e) => {
                    e.stopPropagation()
                    onDeleteClick()
                  }}
                />
              </DeleteIcon>
            )}
          </div>
        </AccordionSummary>

        <IFButton
          color={Colors.primary}
          isFill={true}
          text={t('CardListItem.Done')}
          onClick={handleDone}
          size="xsm"
          icon={
            <Icon
              sx={{ width: '20px', height: '20px' }}
              component={CheckIcon}
            />
          }
          isLoading={isUpdateLoading}
        />
        <IFButton
          color={Colors.primary}
          isFill={false}
          text={t('CardListItem.Cancel')}
          onClick={handleCancel}
          size="xsm"
          className={Styles.Button}
          icon={<CloseIcon className={Styles.ButtonIcon} />}
        />
      </Accordion>
    )
  },
)
CardListItem.propTypes = {
  contractId: PropTypes.string,
  uid: PropTypes.string,
  type: PropTypes.string,
  status: PropTypes.string,
  onDeleteClick: PropTypes.func,
  isUpdateLoading: PropTypes.bool,
  onUpdateClick: PropTypes.func,
  editPermission: PropTypes.bool,
}

export default CardListItem
